import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormHelperText,
  useMediaQuery,
  useTheme,
  MenuItem,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config.js";
import { Country, State } from "country-state-city";
import { useUser } from "../../pages/login/UserContextPersistent";
import CircularProgress from "@mui/material/CircularProgress";

const WORKSPACE_NAME_REQUIRED = "WORKSPACE_NAME_REQUIRED";

const WorkspaceHostExample = () => (
  <>
    <Box component="span" sx={{ fontWeight: 700 }}>
      mycompany
    </Box>
    .{config.tenantBaseDomain}
  </>
);

export default function Register() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { dispatch } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [registerData, setRegisterData] = useState({
    organizationName: "",
    organizationWebsite: "",
    subdomainSlug: "",
    country: "",
    state: "",
    contactName: "",
    address: "",
    email: "",
    phoneNumber: "",
    organizationStatus: "INACTIVE",
    city: "",
    pincode: "",
    organizationType: [],
    organizationDetails: "",
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hasNoStates, setHasNoStates] = useState(false);

  const organizationTypes = [
    { value: "vehicleTrack", label: "Vehicle Tracking" },
    { value: "staffTrack", label: "Staff Tracking" },
    { value: "schoolTrack", label: "School Bus Tracking" },
    { value: "passengerApp", label: "Passenger Web App" },
    { value: "custom", label: "Custom Solution" },
  ];

  const [errors, setErrors] = useState({
    organizationName: "",
    organizationWebsite: "",
    subdomainSlug: "",
    country: "",
    state: "",
    contactName: "",
    address: "",
    email: "",
    phoneNumber: "",
    city: "",
    pincode: "",
    organizationType: "",
    organizationDetails: "",
  });

  const [touched, setTouched] = useState({
    organizationName: false,
    organizationWebsite: false,
    subdomainSlug: false,
    country: false,
    state: false,
    contactName: false,
    address: false,
    email: false,
    phoneNumber: false,
    city: false,
    pincode: false,
    organizationType: false,
    organizationDetails: false,
  });

  const [backendErrors, setBackendErrors] = useState({
    email: "",
    domain: "",
    general: "",
  });

  // Load countries on component mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Update states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const statesList = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(statesList);
      if (statesList.length === 0) {
        setHasNoStates(true);
        setRegisterData((prev) => ({
          ...prev,
          state: "N/A", // Set default value for countries with no states
        }));
      } else {
        setHasNoStates(false);
        setRegisterData((prev) => ({
          ...prev,
          state: "",
        }));
      }
    } else {
      setStates([]);
      setHasNoStates(false);
      setRegisterData((prev) => ({
        ...prev,
        state: "",
      }));
    }
  }, [selectedCountry]);

  const assignDefaultFeatures = async (organizationId) => {
    try {
      const featuresResponse = await axios.get(`${config.baseURL}/feature/`, {
        withCredentials: true,
      });

      const allFeatures = featuresResponse.data;

      const mandatoryCategories = ["vehicle", "stop", "route", "admin"];

      // Filter features that belong to mandatory categories and are default
      const defaultFeatureIds = allFeatures
        .filter((feature) => {
          const pathParts = feature.api_path.split("/");
          const basePath = pathParts[2];

          // Check if feature belongs to mandatory categories
          let isMandatoryCategory = false;

          if (mandatoryCategories.includes(basePath)) {
            isMandatoryCategory = true;
          }

          // For admin category, include both 'user' and 'role' paths
          if (basePath === "user" || basePath === "role") {
            isMandatoryCategory = true;
          }

          // Return only default features from mandatory categories
          return isMandatoryCategory && feature.isDefault === true;
        })
        .map((feature) => feature.id);

      console.log("Assigning default feature IDs:", defaultFeatureIds);

      // Assign default features to the organization
      if (defaultFeatureIds.length > 0) {
        const updateResponse = await axios.put(
          `${config.baseURL}/register/${organizationId}`,
          {
            requestedFeatureIds: defaultFeatureIds,
          },
          { withCredentials: true }
        );

        console.log(
          "Default features assigned successfully:",
          updateResponse.data
        );
        return updateResponse.data;
      }
    } catch (error) {
      console.error("Error assigning default features:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      const countryObj = countries.find((country) => country.name === value);
      setSelectedCountry(countryObj);
    }

    setRegisterData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    validateField(name, registerData[name]);
  };

  const validateField = (name, value) => {
    let errorMessage = "";

    switch (name) {
      case "organizationName":
        errorMessage = value ? "" : "Organization Name is required";
        break;
      case "country":
        errorMessage = value ? "" : "Country is required";
        break;
      case "state":
        errorMessage = value || hasNoStates ? "" : "State is required";
        break;
      case "contactName":
        errorMessage = value ? "" : "Contact Name is required";
        break;
      case "email":
        if (!value) {
          errorMessage = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errorMessage = "Email is invalid";
        }
        break;
      case "phoneNumber":
        if (!value) {
          errorMessage = "Phone Number is required";
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ""))) {
          errorMessage = "Phone Number should be 10 digits";
        }
        break;
      case "city":
        errorMessage = value ? "" : "City is required";
        break;
      case "pincode":
        if (!value) {
          errorMessage = "Pincode is required";
        } else if (!/^\d{5,6}$/.test(value)) {
          errorMessage = "Pincode should be 5-6 digits";
        }
        break;
      case "organizationWebsite":
        if (!value) {
          errorMessage = "Organization Website is required";
        } else if (
          !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i.test(
            value
          )
        ) {
          errorMessage = "Please enter a valid website URL (e.g., example.com)";
        }
        break;
      case "subdomainSlug":
        if (!value) {
          errorMessage = WORKSPACE_NAME_REQUIRED;
        } else {
          const normalized = String(value).trim().toLowerCase();
          const slugRegex = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;
          const reserved = new Set([
            "app",
            "api",
            "www",
            "dev",
            "staff",
            "da",
            "admin",
            "dashboard",
            "routes",
            "login",
            "auth",
          ]);
          if (!slugRegex.test(normalized) || normalized.length < 2 || normalized.length > 32) {
            errorMessage = "Workspace name must be 2-32 characters (letters, digits, hyphens)";
          } else if (reserved.has(normalized)) {
            errorMessage = "This workspace name is reserved";
          }
        }
        break;
      case "address":
        errorMessage = value ? "" : "Address is required";
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));

    return !errorMessage;
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = {};
    let newTouched = {};

    const fieldsToValidate = [
      "organizationName",
      "organizationWebsite",
      "subdomainSlug",
      "country",
      "state",
      "contactName",
      "address",
      "email",
      "phoneNumber",
      "city",
      "pincode",
    ];

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      const fieldIsValid = validateField(field, registerData[field]);
      if (!fieldIsValid) {
        isValid = false;
        // newErrors[field] = errors[field] || `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    setTouched(newTouched);
    // setErrors((prevErrors) => ({
    //   ...prevErrors,
    //   ...newErrors,
    // }));
    if (registerData.organizationType.length === 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        organizationType: "Please select at least one solution",
      }));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setErrorMessage("");
    setBackendErrors({
      email: "",
      domain: "",
      general: "",
    });
    setIsLoading(true);

    try {
      const dataToSubmit = {
        name: registerData.organizationName,
        domain: registerData.organizationWebsite,
        subdomainSlug: registerData.subdomainSlug,
        address: registerData.address,
        city: registerData.city,
        country: registerData.country,
        pincode: registerData.pincode,
        contactName: registerData.contactName,
        contactEmail: registerData.email,
        contactPhone: registerData.phoneNumber,
        state: hasNoStates ? "N/A" : registerData.state,
        organizationStatus: "INACTIVE",
        // New FIELDS
        organizationType: registerData.organizationType,
        organizationDetails: registerData.organizationDetails,
      };
      console.log("Submitting data:", dataToSubmit);
      console.log("Organization Type:", dataToSubmit.organizationType);
      dispatch({ type: "CLEAR_USER" });
      const response = await axios.post(
        `${config.baseURL}/register`,
        dataToSubmit,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (response.status === 201) {
        const { adminUser, organization, orgAdmin, registerOrganization } =
          response.data;

        localStorage.setItem("registerOrganizationId", registerOrganization.id);
        localStorage.setItem(
          "registerOrganizationTypes",
          JSON.stringify(dataToSubmit.organizationType || [])
        );
        await assignDefaultFeatures(registerOrganization.id);
        dispatch({
          type: "SET_USER",
          payload: {
            id: adminUser.id,
            name: adminUser.name,
            userName: adminUser.userName,
            email: adminUser.email,
            organizationId: adminUser.organizationId,
            role: adminUser.roleId,
            domain: organization.domain,
            canAlterAccessRole: orgAdmin.canAlterAccessRole,
          },
        });
        dispatch({
          type: "SET_ORGANIZATION",
          payload: {
            name: organization?.name || "",
            id: organization?.id || "",
            organizationStatus: organization?.organizationStatus || "",
            contactName: organization?.contactName || "",
            address: organization?.address || "",
          },
        });
        dispatch({
          type: "SET_FEATURES",
          payload: [],
        });

        setRegisterData({
          organizationName: "",
          organizationWebsite: "",
          subdomainSlug: "",
          address: "",
          city: "",
          country: "",
          pincode: "",
          contactName: "",
          email: "",
          phoneNumber: "",
          organizationStatus: "",
          state: "",
          organizationType: [],
          organizationDetails: "",
        });

        setSuccessMessage("Registration successful");
        navigate("/registerConfirm");
      }
    } catch (error) {
      const errorResponse = error.response?.data?.error || error.message;

      setBackendErrors({
        email: "",
        domain: "",
        general: "",
      });

      if (typeof errorResponse === "string") {
        if (errorResponse.includes("Validation failed:")) {
          const validationErrors = errorResponse
            .split("Validation failed:")[1]
            .split(",")
            .map((err) => err.trim());

          validationErrors.forEach((error) => {
            if (error.includes("email already exists")) {
              setBackendErrors((prev) => ({
                ...prev,
                email: "An organization with this email already exists",
              }));
            }
            if (error.includes("domain already exists")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "An organization with this domain already exists",
              }));
            }
            if (error.includes("Invalid domain format")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "Invalid domain format. Please enter a valid website",
              }));
            }
            if (error.includes("Invalid pincode format")) {
              setErrors((prev) => ({
                ...prev,
                pincode: "Pincode must be 6 digits",
              }));
            }
          });

          const otherErrors = validationErrors
            .filter(
              (err) =>
                !err.includes("email already exists") &&
                !err.includes("domain already exists") &&
                !err.includes("Invalid domain format") &&
                !err.includes("Invalid pincode format")
            )
            .join(", ");

          if (otherErrors) {
            setBackendErrors((prev) => ({
              ...prev,
              general: otherErrors,
            }));
          }
        } else {
          if (errorResponse.includes("domain already exists")) {
            setBackendErrors((prev) => ({
              ...prev,
              domain: "An organization with this domain already exists",
            }));
          } else {
            setBackendErrors((prev) => ({
              ...prev,
              general: errorResponse,
            }));
          }
        }
      } else {
        setBackendErrors((prev) => ({
          ...prev,
          general: "An error occurred during registration. Please try again.",
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const customTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      height: "55px",
      borderRadius: "16px",
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-root": {
      border: "2px solid #183059",
      borderRadius: "16px",
      height: "55px",
      padding: "16px 24px",
    },
    "& .MuiInputBase-root.Mui-error": {
      border: "2px solid #d32f2f",
    },
    "& .MuiInputLabel-root": {
      position: "relative",
      transform: "none",
      marginBottom: "8px",
    },
    marginBottom: "4px",
  };

  const customSelectFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-root": {
      border: "2px solid #183059",
      borderRadius: "16px",
      minHeight: "55px",
      padding: "0px 14px",
    },
    "& .MuiInputBase-root.Mui-error": {
      border: "2px solid #d32f2f",
    },
    "& .MuiSelect-select": {
      padding: "14px 14px",
    },
    "& .MuiInputLabel-root": {
      position: "relative",
      transform: "none",
      marginBottom: "8px",
    },
    marginBottom: "4px",
  };

  const customMultilineTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-root": {
      border: "2px solid #183059",
      borderRadius: "16px",
      padding: "16px 24px",
      minHeight: "55px",
    },
    "& .MuiInputBase-root.Mui-error": {
      border: "2px solid #d32f2f",
    },
    "& .MuiInputLabel-root": {
      position: "relative",
      transform: "none",
      marginBottom: "8px",
    },
    marginBottom: "4px",
  };

  return (
    <Box
      sx={{
        padding: { xs: "16px", sm: "20px", md: "40px" },
        backgroundColor: "#EEEEEE",
      }}
    >
      <Box
        sx={{
          maxWidth: "1360px",
          borderRadius: "20px",
          padding: {
            xs: "20px",
            sm: "40px",
            md: "60px",
            lg: "80px 180px",
          },
          gap: { xs: "32px", md: "64px" },
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
          margin: "0 auto",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                marginBottom: { xs: "16px", sm: "24px", md: "32px" },
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "24px", sm: "28px", md: "36px" },
                lineHeight: "141%",
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: "#183059",
                padding: "10px",
                borderRadius: "8px",
              }}
            >
              Register your Organization & Get Started
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              {(backendErrors.general || errorMessage) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      backgroundColor: "#FDE7E7",
                      color: "#D32F2F",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <Typography>
                      {backendErrors.general || errorMessage}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {successMessage && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      backgroundColor: "#EDF7ED",
                      color: "#1E4620",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <Typography>{successMessage}</Typography>
                  </Box>
                </Grid>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Organization Name (e.g.KINO) "
                    name="organizationName"
                    label="Organization Name"
                    fullWidth
                    value={registerData.organizationName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.organizationName && !!errors.organizationName
                    }
                    helperText={
                      touched.organizationName && errors.organizationName
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Organization Website (e.g.kino.com)"
                    name="organizationWebsite"
                    label="Organization Website"
                    fullWidth
                    value={registerData.organizationWebsite}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.organizationWebsite &&
                      (!!errors.organizationWebsite || !!backendErrors.domain)
                    }
                    helperText={
                      touched.organizationWebsite &&
                      (errors.organizationWebsite || backendErrors.domain)
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="subdomainSlug"
                    label={
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "center" }}
                      >
                        Workspace Name
                        <Tooltip
                          arrow
                          placement="top"
                          enterTouchDelay={0}
                          title={`Add a unique name for your subdomain. Letters, numbers, and hyphens only. Eg: if you add "mycompany" then your subdomain will be mycompany.${config.tenantBaseDomain}.`}
                        >
                          <IconButton
                            size="small"
                            aria-label="Workspace name help"
                            onClick={(e) => e.preventDefault()}
                            onMouseDown={(e) => e.preventDefault()}
                            sx={{ p: 0.25, ml: 0.5, color: "#6B7280" }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    fullWidth
                    value={registerData.subdomainSlug}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.subdomainSlug && !!errors.subdomainSlug}
                    helperText={
                      touched.subdomainSlug &&
                      errors.subdomainSlug === WORKSPACE_NAME_REQUIRED ? (
                        <>
                          Workspace name is required (eg: <WorkspaceHostExample />)
                        </>
                      ) : (
                        touched.subdomainSlug && errors.subdomainSlug
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    InputProps={{
                      startAdornment: !registerData.subdomainSlug ? (
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            left: "24px",
                            right: "24px",
                            pointerEvents: "none",
                            color: "#9CA3AF",
                            fontSize: "16px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Enter Workspace name (<WorkspaceHostExample />)
                        </Box>
                      ) : null,
                    }}
                    inputProps={{
                      "aria-label": "Workspace Name",
                    }}
                    sx={{
                      ...customTextFieldStyles,
                      "& .MuiInputAdornment-root": {
                        margin: 0,
                        maxHeight: "none",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    name="country"
                    label="Country"
                    fullWidth
                    value={registerData.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.country && !!errors.country}
                    helperText={touched.country && errors.country}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customSelectFieldStyles}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected === "" ? (
                          <span style={{ color: "#888" }}>Select Country</span>
                        ) : (
                          selected
                        ),
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Country</em>
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.isoCode} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    name="state"
                    label="State"
                    fullWidth
                    value={registerData.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.state && !!errors.state}
                    helperText={touched.state && errors.state}
                    disabled={hasNoStates}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customSelectFieldStyles}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected === "" ? (
                          <span style={{ color: "#888" }}>Select State</span>
                        ) : (
                          selected
                        ),
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{hasNoStates ? "N/A" : "Select State"}</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.isoCode} value={state.name}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter City (e.g.Bengaluru)"
                    name="city"
                    label="City"
                    fullWidth
                    value={registerData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Pincode (e.g.560001)"
                    name="pincode"
                    label="Pincode"
                    fullWidth
                    value={registerData.pincode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.pincode &&
                      (!!errors.pincode || !!backendErrors.pincode)
                    }
                    helperText={
                      touched.pincode &&
                      (errors.pincode || backendErrors.pincode)
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Address (e.g.123 MG Road)"
                    name="address"
                    label="Address"
                    fullWidth
                    value={registerData.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.address && !!errors.address}
                    helperText={touched.address && errors.address}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Contact Name (e.g.Rahul Sharma)"
                    name="contactName"
                    label="Contact Name"
                    fullWidth
                    value={registerData.contactName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.contactName && !!errors.contactName}
                    helperText={touched.contactName && errors.contactName}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Email (e.g.rahul@example.com)"
                    name="email"
                    label="Email"
                    fullWidth
                    type="email"
                    value={registerData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.email && (!!errors.email || !!backendErrors.email)
                    }
                    helperText={
                      touched.email && (errors.email || backendErrors.email)
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    placeholder="Enter Phone Number (e.g.9876543210)"
                    name="phoneNumber"
                    label="Phone Number"
                    fullWidth
                    value={registerData.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phoneNumber && !!errors.phoneNumber}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customTextFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box>
                    <Typography
                      sx={{
                        marginBottom: "8px",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "16px",
                        color: "#000000DE",
                      }}
                    >
                      What solution you are looking for?
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: "16px",
                        padding: "16px 24px",
                        minHeight: "55px",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      {organizationTypes.map((option) => {
                        const isChecked =
                          registerData.organizationType.includes(option.value);
                        const hasError =
                          errors.organizationType &&
                          registerData.organizationType.length === 0;
                        return (
                          <Box
                            key={option.value}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              const currentTypes = [
                                ...registerData.organizationType,
                              ];
                              const index = currentTypes.indexOf(option.value);
                              if (index > -1) {
                                currentTypes.splice(index, 1);
                              } else {
                                currentTypes.push(option.value);
                              }
                              setRegisterData((prev) => ({
                                ...prev,
                                organizationType: currentTypes,
                              }));
                              if (errors.organizationType) {
                                setErrors((prev) => ({
                                  ...prev,
                                  organizationType: "",
                                }));
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: "20px",
                                height: "20px",
                                marginRight: "8px",
                                cursor: "pointer",
                                border: `2px solid ${
                                  hasError
                                    ? "#d32f2f"
                                    : isChecked
                                    ? "#20A8E0"
                                    : "#95A0B3"
                                }`,
                                backgroundColor: isChecked
                                  ? "#20A8E0"
                                  : "transparent",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {isChecked && (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M2 7L5.5 10.5L12 3.5"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontSize: "16px",
                                color: isChecked ? "#20A8E0" : "#95A0B3",
                                userSelect: "none",
                              }}
                            >
                              {option.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {errors.organizationType && (
                      <FormHelperText
                        error
                        sx={{ marginLeft: "14px", marginTop: "4px" }}
                      >
                        Please select a solution
                      </FormHelperText>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    placeholder="e.g. We are a logistics company looking for fleet management solution"
                    name="organizationDetails"
                    label="Tell us more about your organization"
                    fullWidth
                    multiline
                    rows={4}
                    value={registerData.organizationDetails}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.organizationDetails &&
                      !!errors.organizationDetails
                    }
                    helperText={
                      touched.organizationDetails && errors.organizationDetails
                    }
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        position: "relative",
                        transform: "none",
                        marginBottom: "8px",
                      },
                    }}
                    sx={customMultilineTextFieldStyles}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: { xs: 1, sm: 2 } }}>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  gap={2}
                  sx={{
                    flexDirection: isMobileScreen ? "column" : "row",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: "120px",
                      width: isMobileScreen ? "100%" : "200px",
                      height: "55px",
                      borderRadius: "8px",
                      padding: "16px 24px",
                      background: "#F2F2F3",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      textTransform: "none",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",
                      mb: isMobileScreen ? 1 : 0,
                    }}
                    onClick={() => navigate("/")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isLoading}
                    sx={{
                      minWidth: "120px",
                      width: isMobileScreen ? "100%" : "200px",
                      height: "55px",
                      borderRadius: "8px",
                      padding: "16px 24px",
                      background: isLoading ? "#CCCCCC" : "#F9E719",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      textTransform: "none",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",
                      ":hover": {
                        background: isLoading ? "#CCCCCC" : "#E2D216",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#1F242E" }} />
                    ) : (
                      "Register"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
